/**
 * Proactive rate limit budget tracker.
 *
 * Manages a sliding window counter and priority-based throttling
 * to prevent 429 errors before they happen.
 *
 * Usage:
 * - `rateLimitBudget.record()` — call after each API request
 * - `rateLimitBudget.canProceed(priority)` — check before polling
 * - `rateLimitBudget.onRateLimited(seconds)` — call on 429 response
 */

/** Priority tiers for API requests */
export enum RequestPriority {
  /** Critical: bot status, active orders — never throttled */
  P1 = 1,
  /** Important: positions, health — throttled under pressure */
  P2 = 2,
  /** Deferrable: equity, PnL, metrics, analytics — throttled first */
  P3 = 3,
}

/** Sliding window rate limiter with priority-based throttling */
export class RateLimitBudget {
  private timestamps: number[] = [];
  private readonly windowMs: number;
  private readonly maxRequests: number;
  private pausedUntil = 0;
  private throttleP3 = false;
  private throttleP2 = false;

  /**
   * @param maxRequests - Max requests per window (default 55, leaving headroom from typical 60 limit)
   * @param windowMs - Sliding window duration in ms (default 60s)
   */
  constructor(maxRequests = 55, windowMs = 60_000) {
    this.maxRequests = maxRequests;
    this.windowMs = windowMs;
  }

  /** Record that a request was made */
  record(): void {
    this.timestamps.push(Date.now());
    this.cleanup();
    this.updateThrottleState();
  }

  /**
   * Check if a request at the given priority can proceed.
   *
   * - P1 requests always proceed (critical operations).
   * - P3 requests are throttled first (at 70% capacity).
   * - P2 requests are throttled next (at 85% capacity).
   */
  canProceed(priority: RequestPriority): boolean {
    if (Date.now() < this.pausedUntil && priority !== RequestPriority.P1) {
      return false;
    }
    this.cleanup();
    this.updateThrottleState();
    if (priority === RequestPriority.P1) return true;
    if (priority === RequestPriority.P3 && this.throttleP3) return false;
    if (priority === RequestPriority.P2 && this.throttleP2) return false;
    return this.timestamps.length < this.maxRequests;
  }

  /**
   * Called when a 429 response is received.
   * Pauses non-critical requests for the specified duration.
   */
  onRateLimited(retryAfterSeconds: number): void {
    this.pausedUntil = Date.now() + retryAfterSeconds * 1000;
    this.throttleP3 = true;
    this.throttleP2 = true;
  }

  /** Get current usage stats (for debugging/monitoring) */
  getUsage(): {
    current: number;
    max: number;
    throttleP2: boolean;
    throttleP3: boolean;
  } {
    this.cleanup();
    this.updateThrottleState();
    return {
      current: this.timestamps.length,
      max: this.maxRequests,
      throttleP2: this.throttleP2,
      throttleP3: this.throttleP3,
    };
  }

  /** Remove timestamps outside the sliding window */
  private cleanup(): void {
    const cutoff = Date.now() - this.windowMs;
    this.timestamps = this.timestamps.filter((t) => t > cutoff);
  }

  /** Update throttle flags based on current usage ratio */
  private updateThrottleState(): void {
    const usage = this.timestamps.length / this.maxRequests;
    this.throttleP3 = usage > 0.7; // Start throttling P3 at 70%
    this.throttleP2 = usage > 0.85; // Start throttling P2 at 85%
  }
}

/** Singleton instance used across the application */
export const rateLimitBudget = new RateLimitBudget();
